/* Shared category filters — Filter button opens Area, Price, Sort */
import React, { useMemo, useRef, useEffect } from 'react';
import { FaFilter, FaTimes, FaChevronDown } from 'react-icons/fa';
import { useI18n } from '../contexts/I18n';

const PRICE_OPTIONS = [
  { id: 'any', max: 99999, en: 'Any price', ar: 'أي سعر' },
  { id: '200', max: 200, en: 'Under 200 EGP', ar: 'أقل من ٢٠٠ ج.م' },
  { id: '500', max: 500, en: 'Under 500 EGP', ar: 'أقل من ٥٠٠ ج.م' },
  { id: '1000', max: 1000, en: 'Under 1,000 EGP', ar: 'أقل من ١٬٠٠٠ ج.م' },
  { id: '2000', max: 2000, en: 'Under 2,000 EGP', ar: 'أقل من ٢٬٠٠٠ ج.م' },
];

const SORT_OPTIONS = [
  { sortBy: 'popularity', sortDirection: 'desc', en: 'Best match', ar: 'الأفضل تطابقاً' },
  { sortBy: 'rating', sortDirection: 'desc', en: 'Top rated', ar: 'الأعلى تقييماً' },
  { sortBy: 'price', sortDirection: 'asc', en: 'Lowest price', ar: 'أقل سعراً' },
  { sortBy: 'price', sortDirection: 'desc', en: 'Highest price', ar: 'أعلى سعراً' },
  { sortBy: 'name', sortDirection: 'asc', en: 'Name A–Z', ar: 'الاسم أ–ي' },
];

const DEFAULT_FILTERS = {
  town: 'Filter By Town',
  search: '',
  priceRange: [0, 99999],
  rating: 0,
  sortBy: 'popularity',
  sortDirection: 'desc',
};

function label(lang, en, ar) {
  return lang === 'ar' ? ar : en;
}

function activePriceId(priceRange) {
  const max = priceRange?.[1] ?? 99999;
  return PRICE_OPTIONS.find((p) => p.max === max)?.id || 'any';
}

function sortKey(sortBy, sortDirection) {
  return `${sortBy}:${sortDirection}`;
}

export default function ServiceFiltersPanel({
  filters,
  setFilters,
  towns = [],
  showFilters,
  setShowFilters,
}) {
  const { t, lang } = useI18n();
  const rootRef = useRef(null);

  const townOptions = useMemo(
    () => towns.filter((name, i) => i > 0 && name && name !== 'Filter By Town'),
    [towns]
  );

  const currentSort = SORT_OPTIONS.find(
    (o) => o.sortBy === filters.sortBy && o.sortDirection === filters.sortDirection
  ) || SORT_OPTIONS[0];

  const priceId = activePriceId(filters.priceRange);
  const hasTown = filters.town && filters.town !== 'Filter By Town';
  const hasPrice = priceId !== 'any';
  const activeCount = (hasTown ? 1 : 0) + (hasPrice ? 1 : 0);
  const hasActiveFilters = activeCount > 0;

  const clearAll = () => {
    setFilters((prev) => ({
      ...DEFAULT_FILTERS,
      search: prev.search || '',
    }));
  };

  useEffect(() => {
    if (!showFilters) return undefined;
    const onDoc = (e) => {
      const path = typeof e.composedPath === 'function' ? e.composedPath() : [];
      const inside = path.some(
        (node) =>
          node instanceof Element &&
          (node.classList.contains('svc-fs-wrap') ||
            node.classList.contains('svc-fs') ||
            node.classList.contains('svc-fs-panel') ||
            node.classList.contains('svc-fs-filter-btn'))
      );
      if (!inside) setShowFilters(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [showFilters, setShowFilters]);

  return (
    <div className={`svc-fs-wrap${showFilters ? ' is-open' : ''}`} ref={rootRef}>
      <div className="svc-fs">
        <div className="svc-fs-bar">
          <div className="svc-fs-actions">
            <button
              type="button"
              className={`svc-fs-filter-btn${showFilters ? ' is-open' : ''}${hasActiveFilters ? ' has-active' : ''}`}
              onClick={() => setShowFilters(!showFilters)}
              aria-expanded={showFilters}
            >
              <FaFilter aria-hidden />
              <span>{label(lang, 'Filter', 'تصفية')}</span>
              {hasActiveFilters && <span className="svc-fs-dot">{activeCount}</span>}
              <FaChevronDown className="svc-fs-chevron-btn" aria-hidden />
            </button>
          </div>
        </div>
      </div>

      {showFilters && (
        <div className="svc-fs-panel" role="region" aria-label={label(lang, 'Filters', 'التصفية')}>
          <div className="svc-fs-panel-row svc-fs-panel-row--3">
            <div className="svc-fs-control">
              <label className="svc-fs-control-label" htmlFor="svc-fs-area">
                {label(lang, 'Area', 'المنطقة')}
              </label>
              <div className="svc-fs-select-wrap svc-fs-select-wrap--full">
                <select
                  id="svc-fs-area"
                  className="svc-fs-select"
                  value={filters.town || 'Filter By Town'}
                  onChange={(e) => setFilters((prev) => ({ ...prev, town: e.target.value }))}
                >
                  <option value="Filter By Town">
                    {label(lang, 'All areas', 'كل المناطق')}
                  </option>
                  {townOptions.map((name) => (
                    <option key={name} value={name}>
                      {name}
                    </option>
                  ))}
                </select>
                <FaChevronDown className="svc-fs-chevron" aria-hidden />
              </div>
            </div>

            <div className="svc-fs-control">
              <label className="svc-fs-control-label" htmlFor="svc-fs-price">
                {label(lang, 'Max price', 'أقصى سعر')}
              </label>
              <div className="svc-fs-select-wrap svc-fs-select-wrap--full">
                <select
                  id="svc-fs-price"
                  className="svc-fs-select"
                  value={priceId}
                  onChange={(e) => {
                    const preset = PRICE_OPTIONS.find((p) => p.id === e.target.value);
                    setFilters((prev) => ({
                      ...prev,
                      priceRange: [0, preset?.max ?? 99999],
                    }));
                  }}
                >
                  {PRICE_OPTIONS.map((p) => (
                    <option key={p.id} value={p.id}>
                      {label(lang, p.en, p.ar)}
                    </option>
                  ))}
                </select>
                <FaChevronDown className="svc-fs-chevron" aria-hidden />
              </div>
            </div>

            <div className="svc-fs-control">
              <label className="svc-fs-control-label" htmlFor="svc-fs-sort">
                {label(lang, 'Sort', 'ترتيب')}
              </label>
              <div className="svc-fs-select-wrap svc-fs-select-wrap--full">
                <select
                  id="svc-fs-sort"
                  className="svc-fs-select"
                  value={sortKey(currentSort.sortBy, currentSort.sortDirection)}
                  onChange={(e) => {
                    const [sortBy, sortDirection] = e.target.value.split(':');
                    setFilters((prev) => ({ ...prev, sortBy, sortDirection }));
                  }}
                >
                  {SORT_OPTIONS.map((o) => (
                    <option
                      key={sortKey(o.sortBy, o.sortDirection)}
                      value={sortKey(o.sortBy, o.sortDirection)}
                    >
                      {label(lang, o.en, o.ar)}
                    </option>
                  ))}
                </select>
                <FaChevronDown className="svc-fs-chevron" aria-hidden />
              </div>
            </div>
          </div>

          {hasActiveFilters && (
            <div className="svc-fs-panel-foot">
              <div className="svc-fs-tags">
                {hasTown && (
                  <button
                    type="button"
                    className="svc-fs-tag"
                    onClick={() => setFilters((prev) => ({ ...prev, town: 'Filter By Town' }))}
                  >
                    <span>{filters.town}</span>
                    <FaTimes aria-hidden />
                  </button>
                )}
                {hasPrice && (
                  <button
                    type="button"
                    className="svc-fs-tag"
                    onClick={() =>
                      setFilters((prev) => ({ ...prev, priceRange: [0, 99999] }))
                    }
                  >
                    <span>
                      {label(
                        lang,
                        PRICE_OPTIONS.find((p) => p.id === priceId)?.en,
                        PRICE_OPTIONS.find((p) => p.id === priceId)?.ar
                      )}
                    </span>
                    <FaTimes aria-hidden />
                  </button>
                )}
              </div>
              <button type="button" className="svc-fs-clear svc-fs-clear--panel" onClick={clearAll}>
                {t('clearFilters')}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export { DEFAULT_FILTERS };
